import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { reportsData } from '../data/reports';
import { initialTasks } from '../data/tasks';
import CreateTaskModal from '../components/CreateTaskModal';
import TaskDetailModal from '../components/TaskDetailModal';

const { 
  FiArrowLeft, FiCheckCircle, FiClock, FiTrendingUp, FiLayers, 
  FiSearch, FiPlus, FiCalendar, FiTrash2, FiStar, FiZap, FiChevronRight,
  FiBookOpen, FiShield
} = FiIcons;

const RecommendationsHub = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [tasks, setTasks] = useState(() => {
    const reportTasks = [];
    Object.values(reportsData).forEach(report => {
      report.sections.forEach(section => {
        section.recommendations.forEach((rec, index) => {
          reportTasks.push({
            id: `report-${report.id}-${section.title}-${index}`,
            text: rec,
            category: section.title,
            source: report.assessmentTitle,
            reportId: report.id,
            status: index % 3 === 0 ? 'completed' : index % 3 === 1 ? 'in-progress' : 'todo',
            priority: index % 2 === 0 ? 'High' : 'Medium',
            createdAt: report.completedDate,
            impact: section.score < 60 ? 'Critical' : 'High'
          });
        });
      });
    });
    return [...reportTasks, ...initialTasks];
  });

  const categories = ['all', ...new Set(tasks.map(r => r.category))];

  const criticalTasks = useMemo(() => 
    tasks.filter(t => t.priority === 'High' && t.status !== 'completed').slice(0, 3)
  , [tasks]);

  const filteredTasks = tasks.filter(task => {
    const matchesTab = activeTab === 'all' || task.category === activeTab;
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesSearch = task.text.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         task.source.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesStatus && matchesSearch;
  });

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    todo: tasks.filter(t => t.status === 'todo').length
  };

  const toggleTaskStatus = (id, e) => {
    e.stopPropagation();
    setTasks(prev => prev.map(task => {
      if (task.id === id) {
        const nextStatus = task.status === 'completed' ? 'todo' : 'completed';
        return { ...task, status: nextStatus };
      }
      return task;
    }));
  };

  const deleteTask = (id, e) => {
    e.stopPropagation();
    if (window.confirm('Delete this task?')) {
      setTasks(prev => prev.filter(t => t.id !== id));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-8">
          <Link to="/dashboard" className="text-gray-600 hover:text-blue-600 flex items-center mb-4 transition-colors">
            <SafeIcon icon={FiArrowLeft} className="mr-2" /> Back to Dashboard
          </Link>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Recommendations Hub</h1>
              <p className="text-gray-600">Your personalized roadmap to business excellence.</p>
            </div>
            <div className="flex gap-3">
              <button 
                onClick={() => setIsModalOpen(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-blue-700 shadow-md transition-all flex items-center"
              >
                <SafeIcon icon={FiPlus} className="mr-2" /> New Task
              </button>
            </div>
          </div>
        </header>

        {/* Strategic Focus Section */}
        <div className="mb-12">
          <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
            <SafeIcon icon={FiZap} className="text-orange-500" /> Strategic Focus (High Impact)
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {criticalTasks.map((task, i) => (
              <motion.div 
                key={task.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                onClick={() => setSelectedTask(task)}
                className="bg-white p-6 rounded-2xl border-l-4 border-l-orange-500 shadow-sm hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex justify-between items-start mb-3">
                  <span className="text-[10px] font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded uppercase">Urgent</span>
                  <SafeIcon icon={FiChevronRight} className="text-gray-300 group-hover:text-blue-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-4 line-clamp-2 leading-relaxed">{task.text}</h3>
                <div className="flex items-center justify-between mt-auto">
                  <span className="text-[10px] font-medium text-gray-400">{task.category}</span>
                  <button className="text-[10px] font-bold text-blue-600 hover:underline">Get Expert Help</button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Categories</h3>
              <nav className="space-y-1">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveTab(cat)}
                    className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${
                      activeTab === cat ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span className="capitalize">{cat.replace('&', 'and')}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                      activeTab === cat ? 'bg-blue-200' : 'bg-gray-200 text-gray-500'
                    }`}>
                      {tasks.filter(r => cat === 'all' || r.category === cat).length}
                    </span>
                  </button>
                ))}
              </nav>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 text-white text-center">
              <SafeIcon icon={FiShield} className="w-8 h-8 mx-auto mb-3 text-blue-400" />
              <h4 className="font-bold mb-2">Need guidance?</h4>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">Book a 15-minute strategy call to prioritize your action items.</p>
              <button className="w-full py-2 bg-blue-600 rounded-lg text-xs font-bold hover:bg-blue-700 transition-all">
                Schedule Now
              </button>
            </div>
          </div>

          {/* Task List Workspace */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search tasks or sources..." 
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-xl focus:ring-2 focus:ring-blue-500 text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex gap-2">
                {['all', 'todo', 'in-progress', 'completed'].map(status => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-2 rounded-lg text-xs font-bold whitespace-nowrap transition-all border ${
                      statusFilter === status 
                        ? 'bg-slate-900 text-white border-slate-900 shadow-sm' 
                        : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <AnimatePresence mode="popLayout">
                {filteredTasks.map((task) => (
                  <motion.div 
                    layout
                    key={task.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setSelectedTask(task)}
                    className={`bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all cursor-pointer group ${
                      task.status === 'completed' ? 'bg-gray-50/50' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <button 
                        onClick={(e) => toggleTaskStatus(task.id, e)}
                        className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          task.status === 'completed' 
                            ? 'bg-green-500 border-green-500 text-white' 
                            : 'border-gray-300 hover:border-blue-500 bg-white'
                        }`}
                      >
                        {task.status === 'completed' && <SafeIcon icon={FiCheckCircle} className="w-4 h-4" />}
                      </button>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${
                            task.priority === 'High' ? 'bg-red-50 text-red-600' : 'bg-orange-50 text-orange-600'
                          }`}>
                            {task.priority}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{task.category}</span>
                        </div>
                        <h3 className={`text-gray-900 font-bold mb-3 leading-relaxed text-sm ${
                          task.status === 'completed' ? 'line-through text-gray-400' : ''
                        }`}>
                          {task.text}
                        </h3>
                        <div className="flex flex-wrap items-center gap-4 text-[10px] text-gray-500">
                          <div className="flex items-center gap-1.5 font-medium">
                            <SafeIcon icon={FiTrendingUp} className="w-3 h-3" />
                            <span>Source: {task.source}</span>
                          </div>
                          {task.dueDate && (
                            <div className="flex items-center gap-1.5 text-orange-600 font-bold">
                              <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                              <span>Due: {task.dueDate}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2">
                        <button 
                          onClick={(e) => deleteTask(task.id, e)}
                          className="p-2 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                        <div className="text-gray-300 group-hover:text-blue-600 transition-colors">
                          <SafeIcon icon={FiChevronRight} />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {filteredTasks.length === 0 && (
                <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-100">
                  <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <SafeIcon icon={FiBookOpen} className="text-gray-300 text-2xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">No tasks found</h3>
                  <p className="text-gray-500">Try adjusting your filters or complete more assessments.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <CreateTaskModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onAdd={(t) => setTasks([t, ...tasks])} />
      <TaskDetailModal isOpen={!!selectedTask} onClose={() => setSelectedTask(null)} task={selectedTask} />
    </div>
  );
};

export default RecommendationsHub;